var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { driverCapsTemplate } from '../../../../kaylee/DriverCaps';
import { isValid } from '../../../../kaylee/EquipmentStates';
import { Log } from '../../../../log';
import { Sutl } from '../../../../sutl';
import { isHttpDriver, isSocketDriver, isUdpDriver, } from './Driver';
import { DriverLogFactory } from './DriverLog';
import { drivers } from './Drivers';
import { HttpDriverAdapter } from './HttpDriverAdapter';
import { SocketDriverAdapter } from './SocketDriverAdapter';
import { State } from './State';
import { Timeouts } from './Timeouts';
import { UdpDriverAdapter } from './UdpDriverAdapter';
const debugInstances = false;
let id = 1;
const reconnectTimeoutMs = 10000;
const validatedValues = {
    duplicate: 'duplicateChannel',
    incompatible: 'invalidChannel',
    ok: 'ok',
    missing: 'noChannel',
    range: 'invalidChannel',
};
export function getDriver(driverId) {
    for (const [name, Driver] of Object.entries(drivers)) {
        if (Driver.key === driverId) {
            return { name, Driver };
        }
    }
    return undefined;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function translateValidatedValues(x) {
    var _a;
    if (typeof x === 'object') {
        for (const prop of Object.keys(x)) {
            x[prop] = translateValidatedValues(x[prop]);
        }
        return x;
    }
    else {
        return (_a = validatedValues[x]) !== null && _a !== void 0 ? _a : 'invalidChannel';
    }
}
export class EquipmentServiceDriverAdapter {
    constructor(key, eqId, eqType, simulate, ipAddress, username, password, doInteractiveAuthentication, onConnectStatus, onControlStatus, config, storage, roomConfig, equipment, translate) {
        this.key = key;
        this.eqId = eqId;
        this.eqType = eqType;
        this.simulate = simulate;
        this.ipAddress = ipAddress;
        this.username = username;
        this.password = password;
        this.onConnectStatus = onConnectStatus;
        this.onControlStatus = onControlStatus;
        this.config = config;
        this.roomConfig = roomConfig;
        this.equipment = equipment;
        this.translate = translate;
        this.caps = Object.assign({}, driverCapsTemplate);
        this.connectState = null;
        this.receivedConnectStatus = false;
        this.credentials = {};
        this.destroyed = false;
        this.hasSavedConfig = false;
        this.isConfiguring = false;
        this.log = Log.channel('EquipmentServiceDriverAdapter');
        this.savedDriverConfig = { equipmentId: '', equipmentType: '', ipAddress: '' };
        this.savedConfig = { equipmentId: '', equipmentType: '', ipAddress: '' };
        this.timeouts = new Timeouts();
        this.id = id++;
        // bind callbacks
        this.connect = this.connect.bind(this);
        this.onConnectionLost = this.onConnectionLost.bind(this);
        this.sendControlStatus = this.sendControlStatus.bind(this);
        // initialization
        if (doInteractiveAuthentication !== undefined) {
            this.credentials.doInteractiveAuthentication = doInteractiveAuthentication;
        }
        if (password !== undefined) {
            this.credentials.password = password;
        }
        if (username !== undefined) {
            this.credentials.username = username;
        }
        const driverInfo = getDriver(key);
        if (driverInfo === undefined)
            throw new Error(`no driver found for key: ${key}`);
        this.driverName = driverInfo.name;
        this.Driver = driverInfo.Driver;
        const str = debugInstances
            ? `${this.driverName} [${this.id}] ${ipAddress}`
            : `${this.driverName} ${ipAddress}`;
        this.log = new DriverLogFactory(str).channel('EquipmentServiceDriverAdapter');
        this.logFactory = new DriverLogFactory(str);
        this.log.debug(`creating driver for key: ${this.key}${simulate ? ' (simulated)' : ''}`);
        this.driverConfig = this.convertConfigToDriverConfig(this.config);
        this.reconnectTimeout = this.timeouts.create();
        this.storage = new State(storage !== null && storage !== void 0 ? storage : {});
        if (isSocketDriver(this.Driver)) {
            this.driverAdapter = new SocketDriverAdapter(key, this.driverName, this.Driver, this.ipAddress, simulate, this.onConnectionLost, this.sendControlStatus, () => __awaiter(this, void 0, void 0, function* () { return this.configure(); }), this.translate, this.logFactory);
        }
        else if (isHttpDriver(this.Driver)) {
            this.driverAdapter = new HttpDriverAdapter(key, this.driverName, this.Driver, this.ipAddress, simulate, this.onConnectionLost, this.sendControlStatus, this.translate, this.logFactory);
        }
        else if (isUdpDriver(this.Driver)) {
            this.driverAdapter = new UdpDriverAdapter(key, this.driverName, this.Driver, this.ipAddress, simulate, this.onConnectionLost, this.sendControlStatus, this.translate, this.logFactory);
        }
        else {
            throw new Error('unsupported driver type');
        }
        // Broadcast initial (offline) caps.
        this.driverAdapter.computeInitialOfflineCaps(this.config);
        this.caps = this.driverAdapter.caps.value;
        this.onControlStatus(this, 'caps', this.caps);
    }
    get configName() {
        return this.Driver.channelsConfigName;
    }
    // public methods
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.log.debug('connecting');
            this.connectState = 'connecting';
            this.onConnectStatus(this);
            const result = yield this.driverAdapter.connect(this.credentials, this.driverConfig, this.storage.value);
            delete this.credentials.doInteractiveAuthentication;
            if (this.destroyed) {
                return;
            }
            if (result !== 'connected') {
                this.connectState = this.convertInitializationResult(result);
                this.onConnectStatus(this);
                this.reconnectTimeout.set(this.connect, reconnectTimeoutMs);
                return;
            }
            this.driverAdapter.caps.subscribe((prevCaps) => {
                this.caps = this.driverAdapter.caps.value;
                this.onControlStatus(this, 'caps', this.caps);
            });
            yield this.configure();
        });
    }
    destroy() {
        this.timeouts.clearAll();
        this.driverAdapter.disconnect();
        this.destroyed = true;
    }
    getNumActiveCalls(callType) {
        return this.driverAdapter.getNumActiveCalls(callType);
    }
    query(param) {
        this.driverAdapter.query(param);
    }
    set(param, value) {
        this.driverAdapter.set(param, value);
    }
    toString() {
        return `${this.driverName} ${this.ipAddress}`;
    }
    // EquipmentService uses the return value to determine if it can call updateCapsAndOnlineStatus
    // immediately after this function executes. Since we are handling everything asynchronously,
    // we will always return false and then send our caps asynchronously when we're done.
    updateConfig(config) {
        const newDriverConfig = this.convertConfigToDriverConfig(config);
        if (Sutl.objEquals(this.driverConfig, newDriverConfig)) {
            this.log.info(`config didn't change, nothing to do`);
            return false;
        }
        if (this.isConfiguring) {
            this.log.info('currently handling another config, saving this one for later');
            this.savedConfig = config;
            this.savedDriverConfig = newDriverConfig;
            this.hasSavedConfig = true;
            return false;
        }
        this.config = config;
        this.driverConfig = newDriverConfig;
        if (this.connectState !== 'connecting') {
            this.configure(); // runs asynchronously
        }
        return false;
    }
    // private methods
    configure() {
        return __awaiter(this, void 0, void 0, function* () {
            this.isConfiguring = true;
            yield this.driverAdapter.configure(this.driverConfig);
            if (this.destroyed) {
                this.log.debug('aborting configure due to being destroyed');
                this.isConfiguring = false;
                return;
            }
            if (this.hasSavedConfig) {
                this.log.debug('aborting configure because config changed');
                this.isConfiguring = false;
                this.timeouts.set(() => {
                    if (this.isConfiguring)
                        return;
                    this.log.info('handling saved config now');
                    this.config = this.savedConfig;
                    this.driverConfig = this.savedDriverConfig;
                    this.hasSavedConfig = false;
                    this.configure();
                });
                return;
            }
            yield this.driverAdapter.control();
            // Caps changes are handled through subscription to this.caps.
            if (this.destroyed) {
                this.log.debug('aborting configure due to being destroyed');
                this.isConfiguring = false;
                return;
            }
            if (this.hasSavedConfig) {
                this.log.debug('aborting configure because config changed');
                this.isConfiguring = false;
                this.timeouts.set(() => {
                    if (this.isConfiguring)
                        return;
                    this.log.info('handling saved config now');
                    this.config = this.savedConfig;
                    this.driverConfig = this.savedDriverConfig;
                    this.hasSavedConfig = false;
                    this.configure();
                });
                return;
            }
            this.connectState = this.driverAdapter.isValid ? 'online' : 'controlFailed';
            this.onConnectStatus(this);
            this.isConfiguring = false;
        });
    }
    convertConfigToDriverConfig(config) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        let driverConfig = Object.assign({}, config);
        const configName = this.Driver.channelsConfigName;
        if (configName && configName in config) {
            const manualConfig = (_a = config[configName]) === null || _a === void 0 ? void 0 : _a.manual;
            if (manualConfig) {
                driverConfig = Object.assign(Object.assign({}, driverConfig), manualConfig);
                delete driverConfig[configName];
            }
        }
        if ((_c = (_b = config === null || config === void 0 ? void 0 : config.viscaControlChannels) === null || _b === void 0 ? void 0 : _b.manual) === null || _c === void 0 ? void 0 : _c.cameras) {
            driverConfig.cameras = (_d = config.viscaControlChannels.manual) === null || _d === void 0 ? void 0 : _d.cameras;
            delete driverConfig.viscaControlChannels;
        }
        // Special case for Zoom. It is partially using the newer way of doing things and partially
        // using the older way. We need to clean it up and convert it to using top-level channel
        // configuration.
        if ((_e = config === null || config === void 0 ? void 0 : config.zoomControlChannels) === null || _e === void 0 ? void 0 : _e.manual) {
            driverConfig = Object.assign(Object.assign({}, driverConfig), config.zoomControlChannels.manual);
            delete driverConfig.zoomControlChannels;
        }
        // Convert display motion on other equipment to contact closures on this equipment.
        const contactClosures = {};
        for (const eq of this.equipment) {
            for (const motion of Object.values((_g = (_f = eq.config) === null || _f === void 0 ? void 0 : _f.displayMotion) !== null && _g !== void 0 ? _g : {})) {
                for (const control of Object.values((_h = motion.controls) !== null && _h !== void 0 ? _h : {})) {
                    const actions = ((_j = control === null || control === void 0 ? void 0 : control.actions) !== null && _j !== void 0 ? _j : {});
                    this.convertContactClosureActions(actions, contactClosures);
                }
            }
        }
        // Convert shade controls to either contact closures or shade controls on this equipment.
        const shadeControls = {};
        for (const [shadeControlId, shadeControl] of Object.entries((_k = this.roomConfig.shadeControls) !== null && _k !== void 0 ? _k : {})) {
            if (shadeControl.type === 'emittedAction') {
                for (const emittedActions of Object.values((_l = shadeControl.position) !== null && _l !== void 0 ? _l : {})) {
                    this.convertContactClosureActions((_m = emittedActions === null || emittedActions === void 0 ? void 0 : emittedActions.actions) !== null && _m !== void 0 ? _m : {}, contactClosures);
                }
                for (const emittedActions of Object.values((_o = shadeControl.tilt) !== null && _o !== void 0 ? _o : {})) {
                    this.convertContactClosureActions((_p = emittedActions === null || emittedActions === void 0 ? void 0 : emittedActions.actions) !== null && _p !== void 0 ? _p : {}, contactClosures);
                }
            }
            else if (shadeControl.type === 'equipment') {
                if (shadeControl.equipmentId !== this.eqId)
                    continue;
                shadeControls[shadeControlId] = Sutl.deepCopy(shadeControl.config);
            }
            else {
                this.log.error(`unhandled shade control type`);
            }
        }
        // Convert shade presets.
        const shadePresets = {};
        for (const [shadePresetId, shadePreset] of Object.entries((_q = this.roomConfig.shadePresets) !== null && _q !== void 0 ? _q : {})) {
            if (shadePreset.type === 'equipment') {
                if (shadePreset.equipmentId !== this.eqId)
                    continue;
                shadePresets[shadePresetId] = Sutl.deepCopy(shadePreset.config);
            }
            else {
                this.log.error(`unhandled shade preset type`);
            }
        }
        if (Object.keys(contactClosures).length > 0)
            driverConfig.contactClosures = contactClosures;
        if (Object.keys(shadeControls).length > 0)
            driverConfig.shadeControls = shadeControls;
        if (Object.keys(shadePresets).length > 0)
            driverConfig.shadePresets = shadePresets;
        return driverConfig;
    }
    convertContactClosureActions(actions, contactClosures) {
        for (const [actionId, action] of Object.entries(actions)) {
            if (action.equipmentId !== this.eqId)
                continue;
            if (action.type === 'contactClosure') {
                contactClosures[actionId] = Sutl.deepCopy(action.config);
            }
        }
    }
    convertInitializationResult(result) {
        if (result === 'authFailed')
            return 'authFailed';
        if (result === 'connected')
            return 'online';
        return 'connectFailed';
    }
    onConnectionLost() {
        if (!this.destroyed) {
            this.reconnectTimeout.set(this.connect, reconnectTimeoutMs);
        }
    }
    sendControlStatus(param, value) {
        if (this.driverAdapter.phase === 'controlling' ||
            this.driverAdapter.isReconnecting ||
            param === 'configDetected' ||
            param === 'configValidated' ||
            param === 'detected' ||
            param === 'equipmentInfo' ||
            param === 'storage' ||
            param === 'validated') {
            // Until we move the DSP driver validated collection from
            // /equipment/<id>/config/validated to /equipmentStates/<id>/validated, we have to
            // convert their validated responses to the old method. The new method also uses
            // different values for the validation results, so we translate those.
            if (param === 'validated' && this.Driver.channelsConfigName) {
                this.onControlStatus(this, 'configValidated', {
                    path: [this.Driver.channelsConfigName, 'validated'],
                    validated: translateValidatedValues(Sutl.deepCopy(value)),
                });
            }
            else {
                if (param === 'storage')
                    this.storage.update(value);
                this.onControlStatus(this, param, value);
            }
        }
        if (param === 'validated' &&
            this.connectState !== 'authFailed' &&
            this.connectState !== 'connectFailed') {
            // We can't use this.driverAdapter.isValid because it gets updated after
            // validated is sent.
            const newConnectState = isValid(value) ? 'online' : 'controlFailed';
            if (newConnectState !== this.connectState) {
                this.connectState = newConnectState;
                this.onConnectStatus(this);
            }
        }
    }
}
